import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { Trophy, CheckCircle, Circle } from 'lucide-react';
import Navigation from '@/components/Navigation';
import confetti from 'canvas-confetti';

function DailyChallenges() {
  const [challenges, setChallenges] = useState([]);
  const [weeklyChallenges, setWeeklyChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [amountSaved, setAmountSaved] = useState('');

  useEffect(() => {
    loadChallenges();
  }, []);

  const loadChallenges = async () => {
    try {
      const [dailyRes, weeklyRes] = await Promise.all([
        API.get('/challenges'),
        API.get('/challenges/weekly')
      ]);
      setChallenges(dailyRes.data);
      setWeeklyChallenges(weeklyRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load challenges');
      setLoading(false);
    }
  };

  const handleComplete = async (challengeId) => {
    if (!outcome) {
      toast.error('Please describe the outcome');
      return;
    }

    try {
      await API.post('/challenges/complete', {
        challenge_id: challengeId,
        outcome,
        amount_saved: amountSaved ? parseFloat(amountSaved) : 0
      });
      toast.success('Challenge completed! +25 points');
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 }
      });
      setCompletingId(null);
      setOutcome('');
      setAmountSaved('');
      loadChallenges();
    } catch (error) {
      toast.error('Failed to complete challenge');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <Trophy className="text-yellow-500 w-16 h-16 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="challenges-title">Challenges</h1>
          <p className="text-gray-400">Complete challenges to level up your negotiation skills</p>
        </div>

        <div className="mb-8">
          <h2 className="font-['Outfit'] text-2xl font-bold mb-6 flex items-center gap-2">
            <Trophy className="text-yellow-500 w-6 h-6" />
            Weekly Challenges (Extra Points!)
          </h2>
          <div className="space-y-6">
            {weeklyChallenges.map(challenge => (
              <div key={challenge.challenge_id} className="ninja-card border-yellow-500/30" data-testid={`weekly-challenge-${challenge.challenge_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {challenge.completed ? (
                        <CheckCircle className="text-green-500 w-6 h-6" data-testid="challenge-completed-icon" />
                      ) : (
                        <Circle className="text-gray-600 w-6 h-6" data-testid="challenge-pending-icon" />
                      )}
                      <h3 className="font-['Outfit'] text-xl font-bold">{challenge.title}</h3>
                    </div>
                    <p className="text-gray-400 ml-9">{challenge.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Points</div>
                    <div className="text-2xl font-bold text-yellow-500">{challenge.points}</div>
                  </div>
                </div>

                {!challenge.completed && completingId !== challenge.challenge_id && (
                  <Button 
                    onClick={() => setCompletingId(challenge.challenge_id)}
                    className="bg-green-600 hover:bg-green-500"
                    data-testid={`start-challenge-${challenge.challenge_id}`}
                  >
                    Mark as Complete
                  </Button>
                )}

                {completingId === challenge.challenge_id && (
                  <div className="mt-4 p-4 bg-white/5 rounded-lg" data-testid="challenge-completion-form">
                    <div className="mb-4">
                      <Label className="mb-2 block">Describe what you did</Label>
                      <Input
                        value={outcome}
                        onChange={(e) => setOutcome(e.target.value)}
                        placeholder="e.g., Completed 3 salary roleplays this week"
                        className="bg-black/50 border-white/20"
                        data-testid="outcome-input"
                      />
                    </div>
                    <div className="mb-4">
                      <Label className="mb-2 block">Amount Saved (£) (optional)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={amountSaved}
                        onChange={(e) => setAmountSaved(e.target.value)}
                        placeholder="0.00"
                        className="bg-black/50 border-white/20"
                        data-testid="amount-saved-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => handleComplete(challenge.challenge_id)}
                        className="flex-1 bg-green-600 hover:bg-green-500"
                        data-testid="submit-challenge-button"
                      >
                        Submit
                      </Button>
                      <Button 
                        onClick={() => setCompletingId(null)}
                        variant="outline"
                        className="border-white/20 hover:bg-white/10"
                        data-testid="cancel-challenge-button"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="font-['Outfit'] text-2xl font-bold mb-6">Daily Challenges</h2>
          <div className="space-y-6">
            {challenges.map(challenge => (
              <div key={challenge.challenge_id} className="ninja-card" data-testid={`challenge-${challenge.challenge_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {challenge.completed ? (
                        <CheckCircle className="text-green-500 w-6 h-6" data-testid="challenge-completed-icon" />
                      ) : (
                        <Circle className="text-gray-600 w-6 h-6" data-testid="challenge-pending-icon" />
                      )}
                      <h3 className="font-['Outfit'] text-xl font-bold">{challenge.title}</h3>
                    </div>
                    <p className="text-gray-400 ml-9">{challenge.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Points</div>
                    <div className="text-2xl font-bold text-yellow-500">{challenge.points}</div>
                  </div>
                </div>

                {!challenge.completed && completingId !== challenge.challenge_id && (
                  <Button 
                    onClick={() => setCompletingId(challenge.challenge_id)}
                    className="bg-green-600 hover:bg-green-500"
                    data-testid={`start-challenge-${challenge.challenge_id}`}
                  >
                    Mark as Complete
                  </Button>
                )}

                {completingId === challenge.challenge_id && (
                  <div className="mt-4 p-4 bg-white/5 rounded-lg" data-testid="challenge-completion-form">
                    <div className="mb-4">
                      <Label className="mb-2 block">Describe what you did</Label>
                      <Input
                        value={outcome}
                        onChange={(e) => setOutcome(e.target.value)}
                        placeholder="e.g., Called coffee shop, got 10% discount"
                        className="bg-black/50 border-white/20"
                        data-testid="outcome-input"
                      />
                    </div>
                    <div className="mb-4">
                      <Label className="mb-2 block">Amount Saved (£) (optional)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={amountSaved}
                        onChange={(e) => setAmountSaved(e.target.value)}
                        placeholder="0.00"
                        className="bg-black/50 border-white/20"
                        data-testid="amount-saved-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => handleComplete(challenge.challenge_id)}
                        className="flex-1 bg-green-600 hover:bg-green-500"
                        data-testid="submit-challenge-button"
                      >
                        Submit
                      </Button>
                      <Button 
                        onClick={() => setCompletingId(null)}
                        variant="outline"
                        className="border-white/20 hover:bg-white/10"
                        data-testid="cancel-challenge-button"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyChallenges;